import React, { useState } from "react";
import {
  BasePageLayout,
  BaseBox,
  BaseTypography,
  BaseButton,
  BaseTextField,
  BaseSelect,
  BaseOption,
  BasePre,
} from "../../Common/UI/exports";
import {
  addExtension,
  generateFlatSwagger,
  validateSwaggerFile,
  compareSwaggerFiles,
  validateSwagger,
} from "../../APIs/sctkAPI";

const TABS = [
  "Add/Generate",
  "Compliance Check",
  "Compare",
  "Schema Validation",
];
const TMF_NUMERIC_OPTIONS = [
  "620",
  "621",
  "622",
  "629",
  "632",
  "635",
  "637",
  "638",
  "639",
  "640",
  "641",
  "644",
  "645",
  "646",
  "648",
  "651",
  "652",
  "653",
  "654",
  "655",
  "656",
  "663",
  "666",
  "667",
  "669",
  "670",
  "671",
  "672",
  "673",
  "674",
  "676",
  "677",
  "678",
  "679",
  "680",
  "681",
  "683",
  "685",
  "687",
  "691",
  "696",
  "699",
  "700",
  "701",
  "702",
  "720",
  "724",
];

function SCTK() {
  const email = localStorage.getItem("email");

  const [activeTab, setActiveTab] = useState(TABS[0]);

  // ---------- Add/Generate state ----------
  const [apiKey, setApiKey] = useState("");
  const [version, setVersion] = useState("");
  const [extKey, setExtKey] = useState("TEST");
  const [extName, setExtName] = useState("TEST");
  const [urls, setUrls] = useState([""]);
  const [busy, setBusy] = useState({ add: false, gen: false });
  const [msg, setMsg] = useState(null);
  const [hasAddedExtension, setHasAddedExtension] = useState(false);

  // ---------- Validation Tab state ----------
  const [valKey, setValKey] = useState("");
  const [valMarket, setValMarket] = useState("TEST");
  const [valFile, setValFile] = useState(null);
  const [valBusy, setValBusy] = useState(false);
  const [valResult, setValResult] = useState(null);
  const [valMsg, setValMsg] = useState(null);

  // ---------- Compare Tab state ----------
  const [cmpOfficial, setCmpOfficial] = useState(null);
  const [cmpEdited, setCmpEdited] = useState(null);
  const [cmpBusy, setCmpBusy] = useState(false);
  const [cmpResult, setCmpResult] = useState(null);
  const [cmpMsg, setCmpMsg] = useState(null);

  // ---------- Schema Validation Tab state ----------
  const [valSwaggerFile, setValSwaggerFile] = useState(null);
  const [valSwaggerBusy, setValSwaggerBusy] = useState(false);
  const [valSwaggerResult, setValSwaggerResult] = useState(null);
  const [valSwaggerMsg, setValSwaggerMsg] = useState(null);

  // ---------- Helpers ----------
  const stripQuery = (url) => {
    try {
      const u = new URL(url.trim());
      u.search = "";
      return u.toString();
    } catch {
      return url.trim();
    }
  };
  const handleUrlChange = (i, v) =>
    setUrls(urls.map((u, idx) => (idx === i ? stripQuery(v) : u)));
  const addUrlField = () => setUrls([...urls, ""]);
  const removeUrlField = (i) => setUrls(urls.filter((_, idx) => idx !== i));
  const showMessage = (text, type) => setMsg({ text, type });

  const isAddDisabled =
    busy.add ||
    !extKey.trim() ||
    !extName.trim() ||
    !apiKey.trim() ||
    !version.trim() ||
    urls.filter((u) => u.trim()).length === 0;

  const doAddExtension = async () => {
    setBusy({ ...busy, add: true });
    setMsg(null);
    try {
      await addExtension({
        apiKey,
        version,
        extensionKey: extKey,
        extensionName: extName,
        extensionFiles: urls.filter(Boolean).map((u) => ({ url: u })),
      });
      showMessage("Extension added ✔", "success");
      setUrls([""]);
      setHasAddedExtension(true);
    } catch (e) {
      showMessage(e.response?.data?.message || e.message, "error");
    } finally {
      setBusy({ ...busy, add: false });
    }
  };

  const doGenerate = async () => {
    setBusy({ ...busy, gen: true });
    setMsg(null);
    try {
      const apiKeyFull = `TMF${apiKey}`;
      const resp = await generateFlatSwagger({ apiKey: apiKeyFull, version });

      // resp.data is a Blob because generateFlatSwagger sets responseType: "blob"
      const blob = resp.data;

      // Read the JSON text from the blob (currently minified / one-line)
      const text = await blob.text();

      // Parse -> re-stringify with indentation (this is what Cmd+K Cmd+F does)
      const json = JSON.parse(text);
      const prettyBlob = new Blob([JSON.stringify(json, null, 2) + "\n"], {
        type: "application/json",
      });

      // Download the pretty-printed file
      const url = URL.createObjectURL(prettyBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${apiKeyFull}-${version}-WithExtensions.swagger.json`;
      link.click();
      URL.revokeObjectURL(url);

      showMessage("Flat Swagger downloaded ⬇", "success");
      setHasAddedExtension(false);
    } catch (e) {
      showMessage(e.message || "Generation failed", "error");
    } finally {
      setBusy({ ...busy, gen: false });
    }
  };

  const doValidate = async () => {
    setValBusy(true);
    setValMsg(null);
    setValResult(null);
    try {
      const { data } = await validateSwaggerFile(valKey, valMarket, valFile);
      setValResult(data);
      setValMsg({
        text: data.statusMessage || "Validation complete.",
        type: data.compliance === 2 ? "success" : "error",
      });
    } catch (e) {
      setValMsg({
        text: e.response?.data?.message || e.message,
        type: "error",
      });
    } finally {
      setValBusy(false);
    }
  };

  const doCompare = async () => {
    setCmpBusy(true);
    setCmpMsg(null);
    setCmpResult(null);
    try {
      const { data } = await compareSwaggerFiles(
        cmpOfficial,
        cmpEdited,
        "JSON"
      );
      setCmpResult(data);
      setCmpMsg({
        text: data.statusMessage || "Comparison complete.",
        type: data.compliance === 2 ? "success" : "error",
      });
    } catch (e) {
      setCmpMsg({
        text: e.response?.data?.message || e.message,
        type: "error",
      });
    } finally {
      setCmpBusy(false);
    }
  };

  const doValidateSwagger = async () => {
    setValSwaggerBusy(true);
    setValSwaggerMsg(null);
    setValSwaggerResult(null);
    try {
      const { data } = await validateSwagger(valSwaggerFile);
      setValSwaggerResult(data);
      setValSwaggerMsg({
        text: data.statusMessage || "Validation complete.",
        type: "success",
      });
    } catch (e) {
      setValSwaggerMsg({
        text: e.response?.data?.message || e.message,
        type: "error",
      });
    } finally {
      setValSwaggerBusy(false);
    }
  };

  // ---------- Styles ----------
  const tabStyle = (active) => ({
    flex: 1,
    padding: "12px 0",
    background: active ? "#a32416" : "#e5e5e5",
    color: active ? "#fff" : "#000",
    fontWeight: active ? 600 : 400,
    cursor: "pointer",
    marginRight: 4,
    borderRadius: "8px 8px 0 0",
    border: "1px solid rgb(140,140,140)",
    textAlign: "center",
  });

  const statusStyle = (type) => ({
    margin: "12px 0",
    padding: "12px 14px",
    borderRadius: 8,
    fontWeight: 600,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    maxWidth: "100%",
    maxHeight: 180,
    overflow: "auto",
    color: type === "error" ? "#a32416" : "#1b8a1b",
  });

  const btnStyle = {
    flex: 1,
    padding: "12px 0",
    background: "#a32416",
    color: "#fff",
    borderRadius: 8,
    cursor: "pointer",
    marginTop: 10,
    marginRight: 5,
    border: "none",
    fontSize: 16,
  };

  const secondaryBtnStyle = {
    ...btnStyle,
    background: "#e5e5e5",
    color: "#333",
  };

  // ---------- UI ----------
  return (
    <BasePageLayout sidebarItems={[]}>
      <BaseBox sx={{ maxWidth: 800, mx: "auto", mt: -2, px: 3 }}>
        <BaseTypography sx={{ fontSize: "1.8rem", fontWeight: 600, mb: 3 }}>
          SCTK - Swagger Extension Toolkit
        </BaseTypography>

        {/* Tabs */}
        <BaseBox sx={{ display: "flex", mb: 3, gap: 1 }}>
          {TABS.map((tab) => (
            <BaseBox
              key={tab}
              sx={tabStyle(activeTab === tab)}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </BaseBox>
          ))}
        </BaseBox>

        {/* ---------- Tab Contents ---------- */}
        {activeTab === "Add/Generate" && (
          <BaseBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <BaseSelect
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            >
              <BaseOption value="">Select TMF API</BaseOption>
              {TMF_NUMERIC_OPTIONS.map((n) => (
                <BaseOption key={n} value={n}>
                  {n}
                </BaseOption>
              ))}
            </BaseSelect>

            <BaseTextField
              placeholder="Version (e.g. 4.0.0)"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            />

            {/* Scrollable list of URLs */}
            <BaseBox
              sx={{
                maxHeight: "250px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                pr: 1,
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 1,
              }}
            >
              {urls.map((u, i) => (
                <BaseBox
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                    mt: -1
                  }}
                >
                  <BaseTextField
                    placeholder={`Extension URL #${i + 1}`}
                    value={u}
                    onChange={(e) => handleUrlChange(i, e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  {urls.length > 1 && (
                    <BaseButton
                      onClick={() => removeUrlField(i)}
                      sx={{ width: 40, flexShrink: 0 }}
                    >
                      🗑
                    </BaseButton>
                  )}
                </BaseBox>
              ))}
            </BaseBox>

            <BaseBox sx={{ 
              display: "flex", 
              gap: 1,
              mt: -7 
              }}>
              <BaseButton onClick={addUrlField} sx={secondaryBtnStyle}>
                + Add another URL
              </BaseButton>
              <BaseButton
                onClick={doAddExtension}
                disabled={isAddDisabled}
                sx={btnStyle}
              >
                {busy.add ? "Adding…" : "Add Extension"}
              </BaseButton>
              <BaseButton
                onClick={doGenerate}
                disabled={busy.gen || !hasAddedExtension}
                sx={btnStyle}
              >
                {busy.gen ? "Generating…" : "Generate Swagger"}
              </BaseButton>
            </BaseBox>

            {msg && <BaseBox sx={statusStyle(msg.type)}>{msg.text}</BaseBox>}
          </BaseBox>
        )}

        {activeTab === "Compliance Check" && (
          <BaseBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <BaseSelect
              value={valKey}
              onChange={(e) => setValKey(e.target.value)}
            >
              <BaseOption value="">Select TMF API</BaseOption>
              {TMF_NUMERIC_OPTIONS.map((n) => (
                <BaseOption key={n} value={`TMF${n}`}>{`TMF${n}`}</BaseOption>
              ))}
            </BaseSelect>
            <BaseTextField
              type="file"
              onChange={(e) => setValFile(e.target.files[0])}
            />
            <BaseButton
              onClick={doValidate}
              disabled={valBusy || !valKey || !valMarket || !valFile}
              sx={btnStyle}
            >
              {valBusy ? "Validating…" : "Validate"}
            </BaseButton>
            {valResult && (
              <BasePre>{JSON.stringify(valResult, null, 2)}</BasePre>
            )}
            {valMsg && (
              <BaseBox sx={statusStyle(valMsg.type)}>{valMsg.text}</BaseBox>
            )}
          </BaseBox>
        )}

        {activeTab === "Compare" && (
          <BaseBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <BaseTextField
              type="file"
              onChange={(e) => setCmpOfficial(e.target.files[0])}
            />
            <BaseTextField
              type="file"
              onChange={(e) => setCmpEdited(e.target.files[0])}
            />
            <BaseButton
              onClick={doCompare}
              disabled={cmpBusy || !cmpOfficial || !cmpEdited}
              sx={btnStyle}
            >
              {cmpBusy ? "Comparing…" : "Compare"}
            </BaseButton>
            {cmpResult && (
              <BasePre>{JSON.stringify(cmpResult, null, 2)}</BasePre>
            )}
            {cmpMsg && (
              <BaseBox sx={statusStyle(cmpMsg.type)}>{cmpMsg.text}</BaseBox>
            )}
          </BaseBox>
        )}

        {activeTab === "Schema Validation" && (
          <BaseBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <BaseTextField
              type="file"
              onChange={(e) => setValSwaggerFile(e.target.files[0])}
            />
            <BaseButton
              onClick={doValidateSwagger}
              disabled={valSwaggerBusy || !valSwaggerFile}
              sx={btnStyle}
            >
              {valSwaggerBusy ? "Validating…" : "Validate"}
            </BaseButton>
            {valSwaggerResult && (
              <BasePre>{JSON.stringify(valSwaggerResult, null, 2)}</BasePre>
            )}
            {valSwaggerMsg && (
              <BaseBox sx={statusStyle(valSwaggerMsg.type)}>
                {valSwaggerMsg.text}
              </BaseBox>
            )}
          </BaseBox>
        )}
      </BaseBox>
    </BasePageLayout>
  );
}

export default SCTK;
